import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
} from '@angular/core';
import {
  InfoBannerComponent,
  RadioCardComponent,
  RadioCardGroupComponent,
  TagComponent,
} from '@catch/general-ui';
import { ThemingService } from '@catch/services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { collapse } from '../../../../assets/animations/animations';
import { StripeBillingInterval } from '../../../enums/stripe.enum';
import { StripeProduct } from '../../../interfaces/stripe';
import { NumberStepperComponent } from '../../number-stepper/number-stepper.component';
import { translateBillingPeriod } from '../../../utils/billing-period-display.util';

export const CUSTOM_AGENTS_OPTION = '__custom__';

@Component({
  selector: 'app-agents-plan-selector',
  imports: [
    CurrencyPipe,
    InfoBannerComponent,
    NgxSkeletonLoaderModule,
    NumberStepperComponent,
    RadioCardComponent,
    RadioCardGroupComponent,
    TagComponent,
    TranslocoPipe,
  ],
  templateUrl: './agents-plan-selector.component.html',
  styleUrl: './agents-plan-selector.component.scss',
  animations: [collapse],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentsPlanSelectorComponent {
  readonly products = input.required<StripeProduct[]>();
  readonly loaded = input.required<boolean>();
  readonly billingInterval = input.required<StripeBillingInterval>();
  readonly disabled = input(false);
  readonly isCustomCurrent = input(false);
  readonly currentPackagePriceId = input<string | null>(null);
  readonly selectedOption = model<string | null>(null);
  readonly customAgentCount = model(1);

  readonly themingService = inject(ThemingService);
  private readonly transloco = inject(TranslocoService);
  readonly CUSTOM_AGENTS_OPTION = CUSTOM_AGENTS_OPTION;

  readonly singleSeatProduct = computed(
    () =>
      this.products().find((product) => this.maxAgentsOf(product) === 1) ??
      null,
  );

  readonly packageProducts = computed(() =>
    this.products().filter((product) => this.maxAgentsOf(product) !== 1),
  );

  readonly customTotalPrice = computed(
    () =>
      this.monthlyDisplayAmount(this.singleSeatProduct()) *
      this.customAgentCount(),
  );

  readonly showPackageHint = computed(() => this.customAgentCount() >= 9);

  /**
   * Resolves the display title for a catalog price.
   * @param {StripeProduct} product Catalog price
   * @returns {string}
   */
  productTitle(product: StripeProduct): string {
    return product.product?.name ?? product.id;
  }

  /**
   * Returns the billing-cycle display amount in major currency units.
   * @param {StripeProduct | null} product Catalog price
   * @returns {number}
   */
  monthlyDisplayAmount(product: StripeProduct | null): number {
    return product ? product.unit_amount / 100 : 0;
  }

  /**
   * Translated billing-cycle label (e.g. "jedes Jahr" / "alle 4 Monate").
   * @param {StripeProduct | null} product Catalog price
   * @returns {string}
   */
  periodHint(product: StripeProduct | null): string {
    return translateBillingPeriod(
      this.transloco,
      product,
      this.billingInterval(),
    );
  }

  /**
   * Reads max_agents from price or product metadata.
   * @param {StripeProduct} product Catalog price
   * @returns {number}
   */
  maxAgentsOf(product: StripeProduct): number {
    const fromPrice = +(product.metadata?.max_agents ?? 0);
    if (fromPrice > 0) return fromPrice;
    return +(product.product?.metadata?.max_agents ?? 0);
  }
}
